import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";

import {
	Modal,
	Layout,
	Button,
	ErrorBanner,
	ProgressIndicator,
	Icon,
	Tooltip
} from "rhombus-react";

import useActiveCreative from "hooks/useActiveCreative";
import { loadVideoSpecs } from "reducers/creatives/VAST";
import { TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { EmptyDash } from "widgets/grid/cells/EmptyDash";

import "./VideoSpecsModal.css";

const orderedFields = [ // We can map this array to generate the table rows in the correct order
	{
		name: "url",
		label: "File",
	},
	{
		name: "dimensions",
		label: "Dimensions",
	},
	{
		name: "ratio",
		label: "Aspect Ratio",
	},
	{
		name: "duration",
		label: "Duration",
	},
	{
		name: "filesize",
		label: "File Size",
	},
	{
		label: "Video Bitrate",
		name: "videoBitrate",
	},
	{
		name: "audioBitrate",
		label: "Audio Bitrate",
	},
	{
		name: "audioSamplerate",
		label: "Audio Sample Rate",
	},
	{
		name: "framerate",
		label: "Frame Rate",
	},
	{
		name: "videoCodec",
		label: "Video Codec",
	},
	{
		name: "audioCodec",
		label: "Audio Codec",
	},
	{
		name: "audioGain",
		label: "Audio Level",
	},
];

export default function VideoSpecsModal({
	videoId,
	type,
	libraryId,
	returnPath,
}){
	let dispatch = useDispatch()
		, [isLoading, setIsLoading] = useState(true)
		, [videoDetail, setVideoDetail] = useState({ specs:{} })
		, $errorBanner = <p className="info">The following are the original file specs.  Publisher-specific transcodes are generated when the VAST creative is added to a placement in Campaign Manager.</p>
		, errorMessages
		, errorObj = {}
		, name
		;

	useActiveCreative(videoId);

	useEffect(() => {
		if(!videoId || videoId === -1){ return; }
		if(type === TYPES.CREATIVES){
			dispatch(loadVideoSpecs(videoId))
				.then((resp) => {
					setIsLoading(false);
					if(resp.success){
						setVideoDetail(resp.payload.data);
					}
				});
			return;
		}
	}, [videoId]);


	if(type === TYPES.CREATIVES){
		name = videoDetail?.creative?.name || "";
	} else {
		name = videoDetail?.name || "";
	}

	function closeModal(){
		dispatch(push(returnPath));
	}

	if(videoDetail.errors){
		errorObj = Object.assign({}, ...(videoDetail.errors.map((err)=>{
			let idx = orderedFields.findIndex(fld => err.field === fld.name);
			if(idx===-1){
				return;
			}
			return {
				[err.field]:{
					label:orderedFields[idx].label,
					error:err.error,
					message:`${orderedFields[idx].label} - ${err.error}`,
				},
			};
		})
		)
		);
	}

	errorMessages = videoDetail?.errors?.map(({ field, error })=>{
		let idx = orderedFields.findIndex(fld =>field===fld.name);
		if(idx===-1){
			return `${field} - ${error}`;
		}
		return `${orderedFields[idx].label} - ${error}`;
	});

	if(errorMessages?.length){
		$errorBanner =  (<ErrorBanner
			title="Video file does not meet the following VAST requirements"
			messages={errorMessages}
		/>);
	}

	function ModalContent(){
		let rows = orderedFields.map(({ name, label }) => {
			let value = videoDetail.specs[name]
				, errorClass = ""
				;

			if(/filesize/gi.test(name)){
				value = formatBytes(value, 1);
			}
			if(/url/gi.test(name)){
				value = <div className="video-url"><a title={videoDetail.specs[name]} href={videoDetail.specs[name]} target="_BLANK" download>Download Asset</a></div>;
			}

			if(!value || value === ""){ value = <EmptyDash />; }

			if(errorObj[name]){
				errorClass = "error";
				value = (
					<div className="value-with-error">
						<span className="value">{value}</span>
						<Tooltip description={errorObj[name].error} direction="right" theme="red">
							<Icon name="clearCircle" className="valueError" />
						</Tooltip>
					</div>);
			}

			return (
				<tr key={name}>
					<td className={`name-col ${errorClass}`}>{label}</td>
					<td className={`spec-value ${errorClass}`}>{value}</td>
				</tr>
			);
		});

		return (
			<div>
				{!isLoading?$errorBanner:null}
				<div className="specs-container">
					<ProgressIndicator isLoading={isLoading} />
					<table className="basic-table video-specs-table">
						<tbody>
							{rows}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	function ModalFooter(){
		return (
			<Layout.ModalFooter>
				<Button onClick={closeModal}>
					Close
				</Button>
			</Layout.ModalFooter>
		);
	}

	let modalProps = {
		title: `Video Specs: ${name}`,
		type: "modal",
		className: "video-specs-modal",

		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},

		renderContent: ModalContent,
		renderFooter: ModalFooter,

		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}

VideoSpecsModal.displayName = "VideoSpecsModal";
VideoSpecsModal.defaultProps = {
	videoId:-1,
	type:TYPES.CREATIVES,
};

VideoSpecsModal.propTypes = {
	type:PropTypes.oneOf([TYPES.ASSETS, TYPES.CREATIVES]).isRequired,
	videoId:PropTypes.number.isRequired,
};
